import { useState, useMemo, memo } from 'react';
import type { SerializedStyles } from '@emotion/react';
import { BaseFieldProps, SearchField } from '@frontend/design-system';
import { useInterval } from '@frontend/design-system';
import { SearchableElements } from '../constants';
import { StyleConfig } from '../global-search-main/types';

interface RotatingSearchFieldProps {
  searchFieldProps: BaseFieldProps<string, '', HTMLInputElement>;
  styleConfig: StyleConfig & {
    searchFieldCss: (open: boolean) => SerializedStyles[];
    searchFieldContainerCss: (open: boolean) => SerializedStyles[];
  };
  isOpen: boolean;
  dataTrackingid: string;
  onSearchSubmit: () => void;
}

const TOTAL_ELEMENTS = SearchableElements.length;
const ROTATION_INTERVAL = 5000;

export const RotatingSearchField = memo(
  ({ searchFieldProps, styleConfig, isOpen, dataTrackingid, onSearchSubmit }: RotatingSearchFieldProps) => {
    const [rotatingIndex, setRotatingIndex] = useState(0);

    const shouldRotate = !isOpen && rotatingIndex < TOTAL_ELEMENTS - 1;

    useInterval(
      () => {
        setRotatingIndex((prev) => prev + 1);
      },
      ROTATION_INTERVAL,
      shouldRotate
    );

    const fieldCss = useMemo(() => styleConfig.searchFieldCss(isOpen), [styleConfig, isOpen]);
    const containerCss = useMemo(() => styleConfig.searchFieldContainerCss(isOpen), [styleConfig, isOpen]);

    const placeholder = useMemo(() => `Search ${SearchableElements[rotatingIndex]}`, [rotatingIndex]);

    return (
      <SearchField
        {...searchFieldProps}
        name='Global Search'
        data-trackingid={dataTrackingid}
        onSearchSubmit={onSearchSubmit}
        placeholder={placeholder}
        clearable={false}
        readOnly
        css={fieldCss}
        containerCss={containerCss}
      />
    );
  }
);

RotatingSearchField.displayName = 'RotatingSearchField';
